import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import ImageCarouselWithOverlay from "./ImageCarousel";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import { MediaFile } from "./listing-page.types";
import { useNavigate } from "react-router-dom";
import ProviderCardContactModal from "../../common/components/provider-card-contactus-modal/ProviderCardContactModal";
import { truncateWords } from "../../utils/utils";
import { toggleProviderFavorite } from "../../api/listing-page.api";
import store from "../../store";
import LikeIcon from "../../assets/icons/like-icon.svg";
import LikedIcon from "../../assets/icons/liked-icon.svg";
import "./provider-card.scss";

export interface ProviderCard {
  id: number;
  title: string;
  location: string;
  description: string;
  // subServices: SubService[];
  price: number;
  images: MediaFile[];
  layout: "grid" | "list";
  businessType: string;
  address: any;
  avgRating: number;
  contactInfo: any;
  phoneNumber: string;
  services: any;
  email: string;
  isFavorite: boolean;
  highlightText?: string;
  onRequestQuote?: (provider: any) => void;
  isCompareSelected?: boolean;
  onCompareToggle?: (providerId: number) => void;
}

export default function ProviderCard({
  id,
  title,
  description,
  // subServices,
  price,
  images,
  layout,
  businessType,
  avgRating,
  address,
  phoneNumber,
  services,
  email,
  contactInfo,
  isFavorite,
  onRequestQuote,
  isCompareSelected,
  onCompareToggle,
}: ProviderCard) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isLoggedIn = store.getState()?.session?.isLoggedIn || false;
  const { city, state } = address || {};

  // LOCATION from localStorage
  const storedLocation = localStorage.getItem("user_location");
  let latitude = null;
  let longitude = null;

  if (storedLocation) {
    const loc = JSON.parse(storedLocation);
    latitude = loc.latitude;
    longitude = loc.longitude;
  }

  // STATES
  const [openModal, setOpenModal] = useState(false);
  const handleOpen = () => setOpenModal(true);
  const handleClose = () => setOpenModal(false);

  const visibleCount = layout === "grid" ? 2 : isMobile ? 2 : 3;

  const subServices = services.map((subService: any) => subService.name);
  const [favorite, setFavorite] = useState(isFavorite);
  const navigate = useNavigate();

  const toggleFavorite = async (providerId: number) => {
    if (!isLoggedIn) return;

    try {
      const response = await toggleProviderFavorite(providerId);

      if (response?.data.success) {
        const isFavorite = response?.data?.data?.isFavorite;
        setFavorite(isFavorite);
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  // mobile view + list view
  if (isMobile && layout === "list") {
    return (
      <Box className="funeral-home-card mobile-list-view">
        <ProviderCardContactModal
          open={openModal}
          onClose={handleClose}
          title={title}
          address={address}
          latitude={contactInfo.latitude}
          longitude={contactInfo.longitude}
          phoneNumber={email || ""}
          email={phoneNumber || ""}
          contactInfo={contactInfo}
        />

        {/* Image */}
        <ImageCarouselWithOverlay
          images={images.length > 0 ? images : []}
          avgRating={avgRating}
          businessType={businessType}
          isFavorite={favorite}
          onFavoriteToggle={() => toggleFavorite(id)}
          showFavoriteIcon={false}
        />

        {/* Info Section */}
        <Box className="center-right-part">
          <Box className="card-info">
            <Typography variant="h6" className="card-info-title">
              {title}
            </Typography>

            <Typography variant="body2" className="card-info-location">
              {city && state ? `${city}, ${state}` : "-"}
            </Typography>

            <Typography variant="body2" className="description">
              {description}
            </Typography>

            {/* Subservices */}
            <Box className="tags">
              {subServices.slice(0, 2).map((subService: string, idx: number) => (
                <span key={idx} className="tag">
                  {subService}
                </span>
              ))}
              {subServices.length > 2 && (
                <span className="tag">+{subServices.length - 2}</span>
              )}
            </Box>
          </Box>

          <Box className="card-actions mobile-list-actions">
            <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", mb: 0.5 }}>
              <Typography
                onClick={(e) => {
                  e.stopPropagation();
                  onCompareToggle?.(id);
                }}
                sx={{
                  fontSize: "14px",
                  color: isCompareSelected ? "#104145" : "#15585D",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  fontWeight: isCompareSelected ? 600 : 400,
                  textDecoration: "underline",
                }}
              >
                {isCompareSelected ? "✓ Added" : "+ Add to compare"}
              </Typography>
            </Box>
            <Typography
              className="price-title"
            >
              Starting Price
            </Typography>
            <Typography
              className="price"
            >
              ${price ?? 0}.00
            </Typography>
            <Box
              className="action-buttons"
            >
              <Button
                fullWidth
                size="large"
                className="contact-info"
                onClick={handleOpen}
              >
                Contact Info
              </Button>
              <Button
                fullWidth
                size="large"
                className="view-details"
                onClick={() => {
                  navigate(`details/${id}`);
                }}
              >
                View Details
              </Button>
            </Box>
            <Button
              fullWidth
              size="large"
              className="request-quote"
              onClick={(e) => {
                e.stopPropagation();
                if (onRequestQuote) {
                  onRequestQuote({
                    id,
                    businessName: title,
                    services,
                  });
                }
              }}
            >
              Request Quote
            </Button>
          </Box>
        </Box>
      </Box >
    );
  }

  // desktop/mobile grid
  return (
    <Box className="funeral-home-card">
      <ProviderCardContactModal
        open={openModal}
        onClose={handleClose}
        title={title}
        address={address}
        latitude={contactInfo.latitude}
        longitude={contactInfo.longitude}
        phoneNumber={phoneNumber}
        email={email}
        contactInfo={contactInfo}
      />

      {/* Image */}
      <ImageCarouselWithOverlay
        images={images.length > 0 ? images : []}
        avgRating={avgRating}
        businessType={businessType}
        isFavorite={favorite}
        onFavoriteToggle={() => toggleFavorite(id)}
        showFavoriteIcon={layout === "grid"}
      />

      {/* Center part */}
      <Box className="card-info">
        <Box
          className={`title-location ${layout === "grid" ? "grid-view" : ""}`}
        >
          <Box className="title-location-box">
            <Box className="title-rating-wrapper">
              <Typography
                variant="h6"
                className="title-box"
                sx={{
                  fontWeight: 600,
                  fontSize: isMobile ? "16px" : "14px",
                  marginBottom: isMobile ? "0.2rem" : "",
                }}
              >
                {title}
              </Typography>

              {layout === "grid" && (
                <Box className="rating-box">
                  {avgRating && <StarRoundedIcon fontSize="small" sx={{ color: "#e9ae10" }} />}
                  <Typography variant="body2">
                    {avgRating !== null && avgRating !== undefined
                      ? avgRating.toFixed(1)
                      : ""}
                  </Typography>
                </Box>
              )}
            </Box>

            <Typography
              variant="body2"
              className="location"
              sx={{
                marginBottom: isMobile ? "0.2rem" : "",
              }}
            >
              {city && state ? `${city}, ${state}` : "-"}
            </Typography>
          </Box>
        </Box>

        <Tooltip title={description} arrow slotProps={{
        popper: {
          sx: {
            [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
              {
                marginTop: '-12px',
                fontSize: "15px"
              },
          },
        },
      }}>
           <Typography className="description">{truncateWords(description ?? "", 10)}</Typography>
        </Tooltip>

        <Box className="tags">
          {subServices.slice(0, visibleCount).map((subService: string, idx: number) => (
            <span key={idx} className="tag">
              {subService}
            </span>
          ))}

          {subServices?.length > visibleCount && (
            <span className="tag">+{subServices.length - visibleCount}</span>
          )}
        </Box>
      </Box>

      {/* Price & Buttons */}
      <Box className="card-actions">
        {layout !== "grid" && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", mb: 0.5 }}>
            <Typography
              onClick={(e) => {
                e.stopPropagation();
                onCompareToggle?.(id);
              }}
              sx={{
                fontSize: "14px",
                color: isCompareSelected ? "#104145" : "#15585D",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                fontWeight: isCompareSelected ? 600 : 400,
                textDecoration: "underline",
              }}
            >
              {isCompareSelected ? "✓ Added" : "+ Add to compare"}
            </Typography>
          </Box>
        )}
        <Box
          className="prices"
          sx={{
            margin: isMobile ? "0px !important" : "",
            marginBottom: isMobile ? "0.5rem !important" : "",
          }}
        >
          <Typography
            className="price-title"
            sx={{
              fontSize: isMobile ? "16px !important" : "",
            }}
          >
            Starting Price
          </Typography>
          <Typography className="price">$ {price ?? 0}.00</Typography>
        </Box>
        <Box className="action-buttons">
          {layout === "grid" ? (
            <>
              <Button
                size={isMobile ? "large" : "small"}
                className="view-details"
                sx={{
                  minWidth: isMobile ? "50%" : "",
                  minHeight: isMobile ? "37.84px" : "",
                }}
                onClick={() => navigate(`details/${id}`)}
              >
                View Details
              </Button>
              <Button
                size={isMobile ? "large" : "small"}
                className="contact-info"
                onClick={handleOpen}
                sx={{
                  minWidth: isMobile ? "50%" : "",
                }}
              >
                Contact Info
              </Button>
            </>
          ) : (
            <>
              <Button
                size="small"
                className="contact-info"
                onClick={handleOpen}
              >
                Contact Info
              </Button>

              <Button
                size="small"
                className="view-details"
                onClick={() => {
                  navigate(`details/${id}`);
                }}
              >
                View Details
              </Button>
            </>
          )}
        </Box>

        {layout === "grid" ? (
          <Box className="action-buttons" sx={{ mt: 1 }}>
            <Button
              size={isMobile ? "large" : "small"}
              className="view-details"
              sx={{
                minWidth: isMobile ? "50%" : "",
                minHeight: isMobile ? "37.84px" : "33px",
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (onRequestQuote) {
                  onRequestQuote({
                    id,
                    businessName: title,
                    services,
                  });
                }
              }}
            >
              Request Quote
            </Button>
            <Button
              size={isMobile ? "large" : "small"}
              className="contact-info"
              sx={{
                minWidth: isMobile ? "50%" : "",
                minHeight: isMobile ? "37.84px" : "33px",
              }}
              onClick={(e) => {
                e.stopPropagation();
                onCompareToggle?.(id);
              }}
            >
              {isCompareSelected ? "✓ Added" : "+ Add to compare"}
            </Button>
          </Box>
        ) : (
          <Button
            fullWidth
            size="small"
            className="request-quote"
            sx={{
              mt: 1,
              backgroundColor: "#ebd28d !important",
              color: "#104145 !important",
              textTransform: "capitalize",
              borderRadius: "8px",
              minHeight: "33px",
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (onRequestQuote) {
                onRequestQuote({
                  id,
                  businessName: title,
                  services,
                });
              }
            }}
          >
            Request Quote
          </Button>
        )}
      </Box>
    </Box>
  );
}
